import React, { useRef, useState } from 'react';
import apiClient from '../api/client';
import { apiError } from './format';
import { useDash } from './DashboardShell';

export const PROOF_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp';
const MAX_MB = 5;
const OK_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

/** Quick check before uploading; the server re-checks the real file type. Returns an error or ''. */
export function proofError(file) {
  if (!file) return '';
  if (file.size > MAX_MB * 1024 * 1024) return `That file is over ${MAX_MB} MB`;
  if (file.type && !OK_TYPES.includes(file.type)) return 'Use a PDF, JPG, PNG or WebP file';
  return '';
}

export async function uploadProof(payoutId, file) {
  const body = new FormData();
  body.append('file', file);
  const { data } = await apiClient.post(`/admin/payouts/${payoutId}/proof`, body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** Open a proof in a new tab. The file needs the bearer token, so it is fetched as a blob first. */
export const ProofButton = ({ url, label = 'View proof' }) => {
  const { notify } = useDash();
  const [busy, setBusy] = useState(false);

  const open = async () => {
    // Open the tab inside the click so the browser does not treat it as a blocked popup.
    const tab = window.open('', '_blank');
    setBusy(true);
    try {
      const res = await apiClient.get(url, { responseType: 'blob' });
      const href = URL.createObjectURL(res.data);
      if (tab) {
        tab.opener = null;
        tab.location.href = href;
      } else {
        window.location.assign(href);
      }
      setTimeout(() => URL.revokeObjectURL(href), 120000);
    } catch (err) {
      if (tab) tab.close();
      // A blob error body is not JSON-parsed by axios; fall back to a plain message.
      notify(err?.response?.status === 404 ? 'No proof of payment found' : apiError(err, 'Could not open the proof'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button type="button" className="ds-proof" onClick={open} disabled={busy}>
      {busy ? 'Opening…' : label}
    </button>
  );
};

/**
 * Proof column for one payout. Everyone with access can view it; only admins (`canUpload`)
 * can attach or replace it.
 */
export const ProofCell = ({ payout, base, canUpload, onChanged }) => {
  const { notify } = useDash();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const problem = proofError(file);
    if (problem) return notify(problem, 'error');
    if (!file) return;
    setBusy(true);
    try {
      await uploadProof(payout.id, file);
      notify('Proof of payment saved');
      onChanged();
    } catch (err) {
      notify(apiError(err, 'Could not upload the proof'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      {payout.has_proof ? <ProofButton url={`${base}/payouts/${payout.id}/proof`} /> : <span className="ds-muted">{canUpload ? 'None' : 'Not attached'}</span>}
      {canUpload && (
        <>
          <input ref={input} type="file" accept={PROOF_ACCEPT} onChange={pick} className="ds-sr" tabIndex={-1} aria-label={`Choose proof for payout ${payout.id}`} />
          <button type="button" className="ds-proof" disabled={busy} onClick={() => input.current?.click()}>
            {busy ? 'Uploading…' : payout.has_proof ? 'Replace' : 'Add proof'}
          </button>
        </>
      )}
    </span>
  );
};
